import { useState, useEffect, useCallback } from 'react';
import { useDevice } from './useDevice';

interface PlaybackState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  mediaId?: string;
  mediaTitle?: string;
}

interface RemoteCommand {
  type: 'play' | 'pause' | 'seek' | 'volume' | 'next' | 'previous' | 'stop';
  value?: number;
  timestamp: number;
}

export function useCompanion() {
  const { deviceType } = useDevice();
  const [playbackState, setPlaybackState] = useState<PlaybackState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
  });
  const [isController, setIsController] = useState(false);
  const [isReceiver, setIsReceiver] = useState(false);

  const syncPlaybackState = useCallback((state: Partial<PlaybackState>) => {
    const newState = { ...playbackState, ...state };
    setPlaybackState(newState);
    
    localStorage.setItem('playback_state', JSON.stringify({
      ...newState,
      timestamp: Date.now(),
    }));

    window.dispatchEvent(new CustomEvent('playback-state-change', { 
      detail: newState 
    }));
  }, [playbackState]);

  const sendCommand = useCallback((command: RemoteCommand) => {
    const commandData = {
      ...command,
      timestamp: Date.now(),
    };

    localStorage.setItem('remote_command', JSON.stringify(commandData));
    window.dispatchEvent(new CustomEvent('remote-command', { detail: commandData }));
  }, []);

  const play = () => sendCommand({ type: 'play', timestamp: Date.now() });
  const pause = () => sendCommand({ type: 'pause', timestamp: Date.now() });
  const seek = (time: number) => sendCommand({ type: 'seek', value: time, timestamp: Date.now() });
  const setVolume = (volume: number) => sendCommand({ type: 'volume', value: volume, timestamp: Date.now() });
  const next = () => sendCommand({ type: 'next', timestamp: Date.now() });
  const previous = () => sendCommand({ type: 'previous', timestamp: Date.now() });
  const stop = () => sendCommand({ type: 'stop', timestamp: Date.now() });

  useEffect(() => {
    if (!isController) return;

    const handleStateChange = (event: Event) => {
      const customEvent = event as CustomEvent<PlaybackState>;
      setPlaybackState(customEvent.detail);
    };

    const pollInterval = setInterval(() => {
      const stateStr = localStorage.getItem('playback_state');
      if (stateStr) {
        const state = JSON.parse(stateStr);
        if (Date.now() - state.timestamp < 10000) {
          setPlaybackState(state);
        }
      }
    }, 1000);

    window.addEventListener('playback-state-change', handleStateChange);

    return () => {
      window.removeEventListener('playback-state-change', handleStateChange);
      clearInterval(pollInterval);
    };
  }, [isController]);

  useEffect(() => {
    if (!isReceiver) return;

    const handleCommand = (event: Event) => {
      const customEvent = event as CustomEvent<RemoteCommand>;
      window.dispatchEvent(new CustomEvent('media-command', { 
        detail: customEvent.detail 
      }));
    };

    window.addEventListener('remote-command', handleCommand);

    return () => {
      window.removeEventListener('remote-command', handleCommand);
    };
  }, [isReceiver]);

  useEffect(() => {
    if (deviceType === 'tv' || deviceType === 'desktop') {
      setIsReceiver(true);
      setIsController(false);
    } else if (deviceType === 'mobile' || deviceType === 'tablet') {
      setIsController(true);
      setIsReceiver(false);
    }
  }, [deviceType]);

  return {
    playbackState,
    isController,
    isReceiver,
    setIsController,
    setIsReceiver,
    syncPlaybackState,
    sendCommand,
    play,
    pause,
    seek,
    setVolume,
    next,
    previous,
    stop,
  };
}

// Keep legacy export for backward compatibility
const KEY = 'companion_url';

export function useCompanionUrl() {
  const [url, setUrlState] = useState<string>(() => localStorage.getItem(KEY) || '');

  useEffect(() => {
    localStorage.setItem(KEY, url || '');
  }, [url]);

  const setUrl = useCallback((next: string) => {
    setUrlState(next);
  }, []);

  return { url, setUrl };
}
